import React from "react";
import Seo from "../components/Seo";

const personSchema = {
    "@context": "https://schema.org",
    "@type": "Person",
    "@id": "https://victorspringer.com/#person",
    "name": "Victor Springer",
    "jobTitle": "Software Engineer (full-stack, backend-heavy)",
    "url": "https://victorspringer.com/cv",
    "image": "https://victorspringer.com/avatar.jpg",
    "description": "Software engineer based in Berlin with a full-stack profile and strong backend focus, with experience in Go, distributed systems, media streaming, Kubernetes, and AWS.",
    "sameAs": [
        "https://linkedin.com/in/victorspringer",
        "https://github.com/victorspringer"
    ],
    "address": {
        "@type": "PostalAddress",
        "addressLocality": "Berlin",
        "addressCountry": "Germany"
    },
    "worksFor": [
        {
            "@type": "Organization",
            "name": "Axel Springer National Media & Tech GmbH & Co. KG",
            "startDate": "2025-08",
            "endDate": "present",
            "jobTitle": "Senior Full-Stack Engineer",
            "description": "Built and extended Go microservices powering live streaming platforms used by BILD and WELT TV."
        },
        {
            "@type": "Organization",
            "name": "Fono.Tech",
            "startDate": "2024-11",
            "endDate": "2025-07",
            "jobTitle": "Senior Full-Stack Engineer (Contract)",
            "description": "Designed backend and mobile architecture for ROXOFF, an immersive audio streaming platform."
        },
        {
            "@type": "Organization",
            "name": "Weavii",
            "startDate": "2023-06",
            "endDate": "2024-03",
            "jobTitle": "Senior Full-Stack Engineer (Founding Engineer)",
            "description": "Designed and implemented backend architecture for a professional networking platform."
        },
        {
            "@type": "Organization",
            "name": "OneFootball",
            "startDate": "2023-02",
            "endDate": "2023-07",
            "jobTitle": "Senior Backend Engineer",
            "description": "Designed video streaming delivery architecture using Bitmovin and built APIs supporting video ingestion and live match highlight clips."
        },
        {
            "@type": "Organization",
            "name": "home24 SE",
            "startDate": "2019-01",
            "endDate": "2022-07",
            "jobTitle": "Senior Backend Engineer",
            "description": "Migrated legacy PHP monolith to Go-based microservices architecture serving web and mobile clients at scale."
        },
        {
            "@type": "Organization",
            "name": "americanas s.a.",
            "startDate": "2016-08",
            "endDate": "2018-12",
            "jobTitle": "Backend Engineer",
            "description": "Developed backend systems supporting one of LATAM's largest e-commerce platforms."
        }
    ],
    "alumniOf": {
        "@type": "CollegeOrUniversity",
        "name": "Universidade Federal do Estado do Rio de Janeiro (UNIRIO)",
        "degree": "Bachelor of Information Systems",
        "startDate": "2014",
        "endDate": "2018"
    },
    "knowsLanguage": [
        "English (Fluent)",
        "German (A2)"
    ],
    "award": [
        "1st place, IronBit (internal hackathon) - americanas s.a., 2018",
        "1st place, AWS GameDay - americanas s.a., 2017"
    ],
    "hasCredential": [
        {
            "@type": "EducationalOccupationalCredential",
            "name": "Leadership: Practical Leadership Skills",
            "educationalProgram": "Udemy",
            "dateAwarded": "2024-05"
        },
        {
            "@type": "EducationalOccupationalCredential",
            "name": "Kubernetes for the Absolute Beginners - Hands-on",
            "educationalProgram": "Udemy",
            "dateAwarded": "2024-06"
        }
    ]
};

const cvPageSchema = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "@id": "https://victorspringer.com/cv",
    "url": "https://victorspringer.com/cv",
    "name": "Victor Springer CV",
    "description": "Curriculum vitae of Victor Springer, a senior backend engineer based in Berlin.",
    "mainEntity": {
        "@id": "https://victorspringer.com/#person"
    }
};

const cvBreadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
        {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://victorspringer.com"
        },
        {
            "@type": "ListItem",
            "position": 2,
            "name": "CV"
        }
    ]
};

const skills = [
    { name: "Go", value: 95 },
    { name: "Distributed Systems", value: 90 },
    { name: "API Architecture", value: 90 },
    { name: "Kubernetes", value: 70 },
    { name: "AWS", value: 85 },
    { name: "GraphQL", value: 80 },
];

const SkillsSection = () => {
    return (
        <section className="sm:w-1/2 mx-auto sm:pr-4">
            <h2 className="text-2xl text-dracula-green border-b-2 border-dracula-comment pb-1 mb-4">
                Skills
            </h2>

            <div className="space-y-4">
                {skills.map((skill) => (
                    <div key={skill.name}>
                        <div className="flex justify-between mb-1">
                            <span className="text-sm font-medium font-semibold text-dracula-foreground">{skill.name}</span>
                        </div>
                        <div className="w-full bg-dracula-comment rounded-full h-4">
                            <div
                                className="h-4 bg-dracula-green rounded-full"
                                style={{ width: `${skill.value}%` }}
                            ></div>
                        </div>
                    </div>
                ))}
            </div>
        </section>
    );
};

const CV = () => {
    return (
        <>
            <Seo
                title="CV"
                description="CV of Victor Springer, a software engineer in Berlin with a full-stack profile and strong backend focus across Go, distributed systems, media streaming, Kubernetes, and AWS."
                structuredData={[personSchema, cvPageSchema, cvBreadcrumbSchema]}
            />

            <header className="max-w-4xl mx-auto text-center">
                <h1 className="text-dracula-pink text-4xl font-semibold mb-4 text-center">Victor Springer<span className="cursor"></span></h1>
                <p className="text-lg">Senior Backend Engineer (Go, Distributed Systems)</p>
                <p className="text-lg">Berlin, Germany</p>
                <p className="text-lg">
                    <a href="https://linkedin.com/in/victorspringer" className="text-dracula-cyan underline">LinkedIn</a>
                </p>
                <p className="text-lg">
                    <a href="https://github.com/victorspringer" className="text-dracula-cyan underline">GitHub</a>
                </p>
            </header>

            <main className="max-w-4xl mx-auto mt-6">
                <section className="mb-6 sm:mb-0">
                    <h2 className="text-2xl text-dracula-green border-b-2 border-dracula-comment pb-1">Summary</h2>
                    <p className="mt-2">
                        Senior Backend Engineer with over 10 years building distributed systems, backend APIs, and high-traffic service platforms.
                        Strong expertise in Go, microservices, Kubernetes, and AWS, delivering scalable backend systems used by millions of users in e-commerce and media streaming platforms.
                    </p>
                </section>
                <div className="sm:flex flex-wrap sm:items-baseline">
                    <SkillsSection />

                    <div className="sm:w-1/2 mx-auto sm:pl-4">
                        <section className="mt-6">
                            <h2 className="text-2xl text-dracula-green border-b-2 border-dracula-comment pb-1">Education</h2>
                            <ul className="list-disc pl-5 mt-2">
                                <li>
                                    <span className="font-semibold">Bachelor of Information Systems</span> - <span className="text-dracula-pink">UNIRIO (Universidade Federal do Estado do Rio de Janeiro)</span>, <span className="text-dracula-yellow">2014 - 2018</span>
                                </li>
                            </ul>
                        </section>

                        <section className="mt-6">
                            <h2 className="text-2xl text-dracula-green border-b-2 border-dracula-comment pb-1">Courses & Certificates</h2>
                            <ul className="list-disc pl-5 mt-2">
                                <li>
                                    <span className="font-semibold">Leadership: Practical Leadership Skills</span> - <span className="text-dracula-pink">Udemy</span>, <span className="text-dracula-yellow">May 2024</span>
                                </li>
                                <li>
                                    <span className="font-semibold">Kubernetes for the Absolute Beginners - Hands-on (Endorsed by Cloud Native Computing Foundation)</span> - <span className="text-dracula-pink">Udemy</span>, <span className="text-dracula-yellow">June 2024</span>
                                </li>
                            </ul>
                        </section>

                        <section className="mt-6">
                            <h2 className="text-2xl text-dracula-green border-b-2 border-dracula-comment pb-1">Languages</h2>
                            <ul className="list-disc pl-5 mt-2">
                                <li>English (Fluent)</li>
                                <li>German (A2)</li>
                            </ul>
                        </section>
                    </div>
                </div>

                <section className="mt-6">
                    <h2 className="text-2xl text-dracula-green border-b-2 border-dracula-comment pb-1 mb-6">Awards</h2>
                    <ul className="list-disc pl-5 mt-2">
                        <li>
                            <span className="font-semibold">1st place, IronBit (Internal Hackathon)</span> - <span className="text-dracula-pink">americanas s.a.</span>, <span className="text-dracula-yellow">2018</span>
                        </li>
                        <li>
                            <span className="font-semibold">1st place, AWS GameDay</span> - <span className="text-dracula-pink">americanas s.a.</span>, <span className="text-dracula-yellow">2017</span>
                        </li>
                    </ul>
                </section>

                <section className="mt-6">
                    <h2 className="text-2xl text-dracula-green border-b-2 border-dracula-comment pb-1 mb-6">
                        Professional Experience
                    </h2>
                    <div className="space-y-6">
                        {[
                            {
                                company: "Axel Springer National Media & Tech GmbH & Co. KG",
                                period: "August 2025 - Present",
                                title: "Senior Full-Stack Engineer",
                                contributions: [
                                    "Built and extended Go microservices powering live streaming platforms used by BILD and WELT TV.",
                                    "Supported streaming pipelines averaging ~20k concurrent HLS sessions and peaking at ~50k during major live events.",
                                    "Developed scheduling systems and operator tooling used in broadcast and OTT workflows.",
                                    "Implemented stream protection and CDN authentication using Terraform and GitOps.",
                                    "Contributed across 10+ services improving reliability, CI pipelines, and operational tooling.",
                                ],
                                tech: [
                                    "Go",
                                    "Kubernetes",
                                    "Terraform",
                                    "AWS",
                                    "Akamai",
                                    "HLS",
                                ],
                            },
                            {
                                company: "Fono.Tech",
                                period: "November 2024 - July 2025",
                                title: "Senior Full-Stack Engineer (Contract)",
                                contributions: [
                                    "Designed backend and mobile architecture for ROXOFF, an immersive audio streaming platform.",
                                    "Implemented streaming, real-time tracking, and in-app purchase infrastructure.",
                                    "Built scalable cloud infrastructure supporting the public launch of the platform.",
                                ],
                                tech: [
                                    "Flutter",
                                    "PHP",
                                    "Firebase",
                                    "GCP",
                                    "Terraform",
                                ],
                            },
                            {
                                company: "Weavii",
                                period: "June 2023 - March 2024",
                                title: "Senior Full-Stack Engineer (Founding Engineer)",
                                contributions: [
                                    "Designed and implemented backend architecture for a professional networking platform.",
                                    "Built GraphQL backend in Go integrating Neo4j and Redis.",
                                    "Developed frontend with TypeScript and Next.js.",
                                    "Deployed infrastructure on AWS using Docker and Terraform.",
                                ],
                                tech: [
                                    "Go",
                                    "GraphQL",
                                    "Neo4j",
                                    "Redis",
                                    "AWS",
                                ],
                            },
                            {
                                company: "OneFootball",
                                period: "February 2023 - July 2023",
                                title: "Senior Backend Engineer",
                                contributions: [
                                    "Designed video streaming delivery architecture using Bitmovin.",
                                    "Built APIs supporting video ingestion and live match highlight clips.",
                                    "Implemented DRM and geo-restriction mechanisms for streaming protection.",
                                ],
                                tech: [
                                    "Go",
                                    "Kubernetes",
                                    "AWS",
                                    "Bitmovin",
                                    "Akamai",
                                ],
                            },
                            {
                                company: "home24 SE",
                                period: "January 2019 - July 2022",
                                title: "Senior Backend Engineer",
                                contributions: [
                                    "Migrated legacy PHP monolith to Go-based microservices architecture serving web and mobile clients at scale.",
                                    "Built Go-based GraphQL APIs serving backend data to web and mobile clients.",
                                    "Developed authentication service based on OAuth 2.0.",
                                    "Mentored junior engineers and apprentices.",
                                ],
                                tech: [
                                    "Go",
                                    "GraphQL",
                                    "AWS",
                                    "Terraform",
                                    "CircleCI",
                                ],
                            },
                            {
                                company: "americanas s.a.",
                                period: "August 2016 - December 2018",
                                title: "Backend Engineer",
                                contributions: [
                                    "Developed backend systems supporting one of LATAM's largest e-commerce platforms.",
                                    "Improved mobile conversion rate by 10 percent through catalog architecture improvements.",
                                    "Reduced page load time by 30 percent through backend performance optimization.",
                                    "Contributed to Black Friday infrastructure supporting record traffic.",
                                ],
                                tech: [
                                    "Go",
                                    "Node.js",
                                    "Elasticsearch",
                                    "AWS",
                                    "Machine Learning",
                                ],
                            },
                            {
                                company: "LD1",
                                period: "January 2015 - July 2016",
                                title: "Junior Full-Stack Developer",
                                contributions: [
                                    "Developed backend services for a clinical engineering SaaS platform.",
                                    "Built Java-based APIs and integrations using Hibernate and SOAP.",
                                ],
                                tech: [
                                    "Java",
                                    "Angular",
                                    "MySQL",
                                    "Hibernate",
                                    "SOAP",
                                ],
                            },
                        ].map((job) => (
                            <article key={job.company} className="bg-dracula-grey p-4 rounded-lg shadow-md">
                                <h3 className="text-xl font-semibold text-dracula-pink">{job.company}</h3>
                                <p className="text-sm text-dracula-yellow">{job.period}</p>
                                <h4 className="text-lg font-semibold mt-1">{job.title}</h4>
                                <ul className="list-disc pl-5 mt-2">
                                    {job.contributions.map((point, idx) => (
                                        <li key={idx}>{point}</li>
                                    ))}
                                </ul>
                                {job.impact && job.impact.length > 0 && (
                                    <>
                                        <h5 className="text-dracula-orange mt-4">Impact</h5>
                                        <ul className="list-disc pl-5 mt-2">
                                            {job.impact.map((point, idx) => (
                                                <li key={idx}>{point}</li>
                                            ))}
                                        </ul>
                                    </>
                                )}
                                <div className="flex flex-wrap gap-2 mt-4" aria-label="Technology stack used">
                                    {job.tech.map((tech, index) => (
                                        <span
                                            key={index}
                                            className="bg-dracula-purple text-dracula-foreground px-3 py-1 text-xs font-semibold rounded-full">
                                            {tech}
                                        </span>
                                    ))}
                                </div>
                            </article>
                        ))}
                    </div>
                </section>
            </main>
        </>
    );
};

export default CV;
